import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ContentLoader from 'components/Common/ContentLoader';
import Toast, { ToastType } from 'components/Common/Toast';
import { useHttp } from 'hooks/use-fetch';
import { useMutation, useQuery } from 'react-query';
import { fetchPendingShareYourLight, approveShareYourLight } from 'pages/AdminPortal/services/shareyourlight.services';
import { handleApiError, trackMixpanelEvent } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';

function CampaignsTab() {
  const { http } = useHttp();
  const [toast, setToast] = React.useState({ open: false, message: '', type: ToastType.success });

  const { data: pendingThoughts, isLoading, refetch } = useQuery('pending-thoughts', fetchPendingShareYourLight(http.get));

  const { submissions } = pendingThoughts ?? {};

  const approveMutation = useMutation((id: string) => approveShareYourLight(
    http.post,
    { id, is_approved: true },
  ), {
    onSuccess: () => {
      setToast({ open: true, message: 'Thought approved!', type: ToastType.success });
      refetch();
    },
    onError: (err) => {
      handleApiError(err, 'CampaignsTab: Failed to approve thought', (msg) => setToast({ open: true, message: msg, type: ToastType.error }));
    },
  });

  const rejectMutation = useMutation((id: string) => approveShareYourLight(
    http.post,
    { id, is_approved: false },
  ), {
    onSuccess: () => {
      setToast({ open: true, message: 'Thought rejected!', type: ToastType.success });
      refetch();
    },
    onError: (err) => {
      handleApiError(err, 'CampaignsTab: Failed to reject thought', (msg) => setToast({ open: true, message: msg, type: ToastType.error }));
    },
  });

  const approveThought = (id: string) => {
    trackMixpanelEvent(MixpanelEventName.CAMPAIGNS_APPROVE_BUTTON_CLICKED, { thoughtId: id });
    approveMutation.mutate(id);
  };

  const rejectThought = (id: string) => {
    trackMixpanelEvent(MixpanelEventName.CAMPAIGNS_REJECT_BUTTON_CLICKED, { thoughtId: id });
    rejectMutation.mutate(id);
  };

  return (
    <ContentLoader isFetching={isLoading} isError={false}>
      <Box>
        <Toast {...toast} onClose={() => setToast({ ...toast, open: false })} />
        <Typography variant='h6' mb={2}>Pending Thoughts</Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '10%' }}>S. No.</TableCell>
                <TableCell sx={{ width: '20%' }}>Title</TableCell>
                <TableCell sx={{ width: '50%' }}>Thought</TableCell>
                <TableCell sx={{ width: '20%' }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(submissions ?? []).map(
                (row: { id: string; content: string; title: string }, index: number) => (
                  <TableRow key={row.id}>
                    <TableCell sx={{ width: '10%' }}>{index + 1}</TableCell>
                    <TableCell sx={{ width: '20%' }} className='fs-mask'>{row.title}</TableCell>
                    <TableCell sx={{ width: '50%' }} className='fs-mask'>{row.content}</TableCell>
                    <TableCell sx={{ width: '20%' }}>
                      <Box display='flex' gap={1}>
                        <Button
                          variant='contained'
                          color='primary'
                          onClick={() => approveThought(row.id)}
                          disabled={approveMutation.isLoading}
                        >
                          Approve
                        </Button>
                        <Button
                          variant='contained'
                          color='error'
                          onClick={() => rejectThought(row.id)}
                          disabled={rejectMutation.isLoading}
                        >
                          Reject
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </ContentLoader>
  );
}

export default CampaignsTab;
